import {
  ListItem as ChakraListItem,
  Stack,
  UnorderedList,
  useDisclosure,
  useToast,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { AlertDialog } from 'components/dialogs/AlertDialog';
import { OverlayProps, TaskOverlayBody } from 'components/TaskOverlay';
import { FirstStepTaskOverlay } from 'components/TaskOverlay/FirstStepTaskOverlay';
import React, { useEffect, useState } from 'react';
import { PayloadError } from 'relay-runtime';
import { RemirrorJSON } from 'remirror';
import { graphql, useMutation } from 'utils/graphClient';
import { isEmptyMessage, stringToRemirrorJson } from 'utils/editor';
import { SuspendWithSpinner } from 'components/SuspendWithSpinner';
import { lazy } from '@loadable/component';
import { CreateAnnouncementOverlay_createDraftAnnouncement_Mutation } from './__generated__/CreateAnnouncementOverlay_createDraftAnnouncement_Mutation.graphql';
import { CreateAnnouncementOverlay_publishAnnouncement_Mutation } from './__generated__/CreateAnnouncementOverlay_publishAnnouncement_Mutation.graphql';
import useEffectOnceWhen from 'hooks/useEffectOnceWhen';
import { CreateAnnouncementOverlay_addAttachmentToAnnouncement_Mutation } from './__generated__/CreateAnnouncementOverlay_addAttachmentToAnnouncement_Mutation.graphql';
import { CreateAnnouncementOverlay_removeLinksFromAnnouncement_Mutation } from './__generated__/CreateAnnouncementOverlay_removeLinksFromAnnouncement_Mutation.graphql';
import Links from '../AnnouncementFormLinks';
import { AttachmentUploadItem } from '../AttachmentUploadItem';
import AttachmentUploadButton from '../AttachmentUploadButton';
import { useFeatureFlag } from 'utils/sessionProvider';
import { useSimpleFileUploader } from 'utils/upload/useSimpleFileUploader';

const RichTextArea = lazy(() => import('components/Editor/RichTextArea'));

const createDraftAnnouncementMutation = graphql`
  mutation CreateAnnouncementOverlay_createDraftAnnouncement_Mutation(
    $message: String!
    $spaceId: ID!
    $links: [AnnouncementLinkInput!]!
  ) {
    createDraftAnnouncement(
      input: { message: $message, spaceId: $spaceId, links: $links }
    ) {
      announcementCreated {
        id
      }
    }
  }
`;

const publishAnnouncementMutation = graphql`
  mutation CreateAnnouncementOverlay_publishAnnouncement_Mutation(
    $id: ID!
    $message: String!
    $connections: [ID!]!
  ) {
    updateAnnouncement(input: { announcementId: $id, message: $message }) {
      announcementUpdated {
        id
      }
    }
    publishAnnouncement(input: { id: $id }) {
      announcement
        @prependNode(connections: $connections, edgeTypeName: "Announcement") {
        id
        ...AnnouncementCard_announcement
      }
    }
  }
`;

interface CreateAnnouncementOverlayProps extends OverlayProps {
  addToConnectionIds: string[];
  spaceId: string;
  linkIds?: string[];
  allowAttachments?: boolean;
}

export const CreateAnnouncementOverlay = ({
  isOpen,
  ...props
}: CreateAnnouncementOverlayProps) => {
  if (isOpen) {
    return <InnerCreateAnnouncementOverlay {...props} isOpen={isOpen} />;
  } else {
    return null;
  }
};

const InnerCreateAnnouncementOverlay = ({
  isOpen,
  onClose,
  spaceId,
  linkIds,
  addToConnectionIds,
  allowAttachments,
}: CreateAnnouncementOverlayProps) => {
  const toast = useToast();
  // Not using initialFocusRef, see below why
  // const initialFocusRef = useRef<HTMLTextAreaElement>(null)
  const [message, setMessage] = useState<RemirrorJSON>(
    stringToRemirrorJson(''),
  );
  const messageChangeHandler = (stringifiedMessage?: string) => {
    const message = JSON.parse(stringifiedMessage || '');
    setMessage(message);
  };
  const [draftAnnouncementId, setDraftAnnouncementId] = useState<
    string | undefined
  >(undefined);

  const shouldAllowAnnouncementsWithAttachments = useFeatureFlag(
    'FEATURE_ANNOUNCEMENTS_WITH_ATTACHMENTS',
  );

  allowAttachments =
    allowAttachments &&
    !linkIds?.length &&
    shouldAllowAnnouncementsWithAttachments;

  const {
    isOpen: isConfirmationDialogOpen,
    onOpen: onOpenConfirmationDialog,
    onClose: onCloseConfirmationDialog,
  } = useDisclosure();

  const [createDraftAnnouncement, creatingDraftAnnouncement] =
    useMutation<CreateAnnouncementOverlay_createDraftAnnouncement_Mutation>(
      createDraftAnnouncementMutation,
    );
  const ensureDraftAnnouncement = () => {
    return new Promise<string>((resolve) => {
      createDraftAnnouncement({
        variables: {
          message: JSON.stringify(stringToRemirrorJson('')),
          spaceId,
          links: (linkIds || []).map((nodeId) => ({ nodeId })),
        },
        onCompleted: ({ createDraftAnnouncement }) => {
          if (createDraftAnnouncement?.announcementCreated) {
            setDraftAnnouncementId(
              createDraftAnnouncement.announcementCreated.id,
            );
            resolve(createDraftAnnouncement.announcementCreated.id);
          }
        },
      });
    });
  };

  useEffectOnceWhen(
    () =>
      !draftAnnouncementId &&
      !creatingDraftAnnouncement &&
      !isEmptyMessage(message),
    ensureDraftAnnouncement,
  );

  const [publishAnnouncement, publishingAnnouncement] =
    useMutation<CreateAnnouncementOverlay_publishAnnouncement_Mutation>(
      publishAnnouncementMutation,
    );

  const handleError = (error: PayloadError | Error) => {
    toast({
      status: 'error',
      title: t`Unable to create announcement`,
      description: error.message || t`Unknown reason`,
    });
  };

  const handlePublish = () => {
    if (!draftAnnouncementId) {
      return;
    }

    publishAnnouncement({
      variables: {
        id: draftAnnouncementId,
        message: JSON.stringify(message),
        connections: addToConnectionIds,
      },
      payloadErrorBehaviour: 'HANDLED',
      onError: handleError,
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          handleError(errors[0]);
        } else if (!response.publishAnnouncement?.announcement.id) {
          handleError({ message: t`Unable to create announcement` });
        } else {
          onCloseConfirmationDialog();
          onClose();
          toast({
            title: t`Announcement created`,
            status: 'success',
            isClosable: true,
          });
        }
      },
    });
  };

  const [amountOfAttachments, setAmountOfAttachments] = useState(0);
  const isEmpty = isEmptyMessage(message) && (allowAttachments && amountOfAttachments === 0);

  return (
    <FirstStepTaskOverlay
      title={t`Announcement`}
      submitButtonTitle={t`Create`}
      isDisabled={isEmpty}
      isOpen={isOpen}
      onClose={onClose}
      isLoading={creatingDraftAnnouncement || publishingAnnouncement}
      onCompleteTask={onOpenConfirmationDialog}
      // Not using initialFocusRef here and disabeling autoFocus. This because we can't get the ref
      //   based approach to work with the lazy loaded RichTextArea. The ref is not loaded fast enough.
      // initialFocusRef={initialFocusRef}
    >
      <TaskOverlayBody my={4}>
        <Stack>
          <SuspendWithSpinner>
            <RichTextArea
              // See above why this line is commented out and autoFocus is enabled
              // ref={initialFocusRef}
              autoFocus
              placeholder={t`Text here`}
              onChange={messageChangeHandler}
              editable
              spaceId={spaceId}
            />
          </SuspendWithSpinner>

          {linkIds && linkIds.length && <Links linkIds={linkIds} />}
          {allowAttachments && (
            <AnnouncementAttachmentsManager
              announcementId={draftAnnouncementId}
              ensureDraftAnnouncement={ensureDraftAnnouncement}
              onAttachmentCountChanged={setAmountOfAttachments}
            />
          )}
        </Stack>
      </TaskOverlayBody>
      <AlertDialog
        title={t`Create announcement`}
        isOpen={isConfirmationDialogOpen}
        onClose={onCloseConfirmationDialog}
        actionButton={{
          title: t`Create`,
          ariaLabel: t`Create announcement`,
          onClick: handlePublish,
          isLoading: publishingAnnouncement,
        }}
        cancelButton={{
          title: t`Continue editing`,
          ariaLabel: t`Cancel`,
          onClick: onCloseConfirmationDialog,
        }}
      >
        {t`This is what will happen:`}
        <UnorderedList>
          <ChakraListItem>{t`This announcement will be visible to all the members of the space.`}</ChakraListItem>
          <ChakraListItem>{t`A notification will be sent to all the members of the space.`}</ChakraListItem>
          <ChakraListItem>
            {t`Once sent, you can still modify or delete the announcement.`}
          </ChakraListItem>
        </UnorderedList>
      </AlertDialog>
    </FirstStepTaskOverlay>
  );
};

interface UploadState {
  key: string;
  localName: string;
  localFile: File;
  uploadCompleted: boolean;
  uploadToken: string | undefined;
  linkId: string | undefined;
  deleted: boolean;
}

const addAttachmentToAnnouncementMutation = graphql`
  mutation CreateAnnouncementOverlay_addAttachmentToAnnouncement_Mutation(
    $announcementId: ID!
    $uploadToken: ID!
  ) {
    addLinksToAnnouncement(
      input: {
        announcementId: $announcementId
        links: [{ attachment: { uploadToken: $uploadToken } }]
      }
    ) {
      announcementUpdated {
        id
      }
      addedLinks {
        id
      }
    }
  }
`;

const removeAttachmentFromAnnouncementMutation = graphql`
  mutation CreateAnnouncementOverlay_removeLinksFromAnnouncement_Mutation(
    $announcementId: ID!
    $linkId: ID!
  ) {
    removeLinksFromAnnouncement(
      input: { announcementId: $announcementId, linkIds: [$linkId] }
    ) {
      announcementUpdated {
        id
      }
    }
  }
`;

const AnnouncementAttachmentsManager = ({
  announcementId,
  ensureDraftAnnouncement,
  onAttachmentCountChanged,
}: {
  announcementId: string | undefined;
  ensureDraftAnnouncement: () => Promise<string>;
    onAttachmentCountChanged: (count: number) => void;
}) => {
  const [selectedFiles, setSelectedFiles] = useState<UploadState[]>([]);
  const notDeletedFilesCount = selectedFiles.filter((file) => !file.deleted).length;

  useEffect(() => {
    onAttachmentCountChanged(notDeletedFilesCount);
  }, [notDeletedFilesCount]);

  const [addLinkToAnnouncement] =
    useMutation<CreateAnnouncementOverlay_addAttachmentToAnnouncement_Mutation>(
      addAttachmentToAnnouncementMutation,
    );

  const [removeLinkFromAnnouncement] =
    useMutation<CreateAnnouncementOverlay_removeLinksFromAnnouncement_Mutation>(
      removeAttachmentFromAnnouncementMutation,
    );

  const { upload } = useSimpleFileUploader('FILES');

  const handleSelectedFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) {
      return;
    }
    let ensuredAnnouncementId = announcementId;
    if (!ensuredAnnouncementId) {
      ensuredAnnouncementId = await ensureDraftAnnouncement();
    }
    if (!ensuredAnnouncementId) {
      throw new Error('Could not create a draft announcement');
    }
    Array.from(e.target.files).forEach((file) => {
      const localFileURL = URL.createObjectURL(file);
      setSelectedFiles((s) => [
        {
          key: Date.now() + localFileURL,
          localFile: file,
          localName: localFileURL,
          uploadToken: undefined,
          uploadCompleted: false,
          linkId: undefined,
          deleted: false,
        },
        ...s,
      ]);
      upload(file).then((uppyFile) => {
        setSelectedFiles((s) =>
          s.map((u) => {
            if (u.localFile === file) {
              return {
                ...u,
                uploadToken: uppyFile.meta.uploadToken as string,
                uploadCompleted: true,
              };
            } else {
              return u;
            }
          }),
        );

        addLinkToAnnouncement({
          variables: {
            announcementId: ensuredAnnouncementId as string,
            uploadToken: uppyFile.meta.uploadToken as string,
          },
          onCompleted: (result) => {
            const newLinkId = (result.addLinksToAnnouncement?.addedLinks ||
              [])[0].id;
            setSelectedFiles((s) =>
              s.map((u) => {
                if (u.uploadToken === (uppyFile.meta.uploadToken as string)) {
                  return {
                    ...u,
                    linkId: newLinkId,
                  };
                } else {
                  return u;
                }
              }),
            );
          },
        });
      });
    });
  };

  const handleRemoveLink = (linkId: string) => {
    setSelectedFiles((s) =>
      s.map((u) => ({
        ...u,
        deleted: u.linkId === linkId,
      })),
    );
    removeLinkFromAnnouncement({
      variables: {
        announcementId: announcementId as string, // At this point it will always exist
        linkId,
      },
    });
  };

  return (
    <Stack>
      <Stack>
        {selectedFiles
          .filter(({ deleted }) => !deleted)
          .map((upload) => (
            <AttachmentUploadIndicator
              key={upload.key}
              upload={upload}
              handleRemoveLink={handleRemoveLink}
            />
          ))}
      </Stack>
      <AttachmentUploadButton onChange={handleSelectedFile} />
    </Stack>
  );
};

const AttachmentUploadIndicator = ({
  upload,
  handleRemoveLink,
}: {
  upload: UploadState;
  handleRemoveLink: (linkId: string) => void;
}) => (
  <AttachmentUploadItem
    uploading={!upload.linkId}
    name={upload.localFile.name}
    mimeType={upload.localFile.type}
    handleRemove={() => handleRemoveLink(upload.linkId as string)}
  />
);
